import * as Sentry from "@sentry/react";
import jwt_decode from "jwt-decode";
import {
  browserName,
  browserVersion,
  deviceType,
  isMobile,
  isTablet,
  osName,
  osVersion,
} from "react-device-detect";

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const SENTRY_ENVIRONMENT = process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV;
const API_URL = process.env.REACT_APP_API_URL;
const MONITORED_RESOURCES = new Set(["events", "orders"]);
const snapshotCache = new Set();
const SENSITIVE_KEY_PATTERN = /authorization|cookie|password|secret|token|api[-_]?key/i;
const URL_LIKE_KEY_PATTERN = /url|uri|endpoint/i;
const MAX_STRING_LENGTH = 500;
const TRACE_PROPAGATION_TARGETS = ["/"];

const asNumber = (value, fallbackValue) => {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallbackValue;
};

const escapeRegExp = (value) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const sanitizeString = (value) =>
  typeof value === "string" && value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}...`
    : value;

const sanitizeUrl = (value) => {
  if (typeof value !== "string" || !value) {
    return value;
  }

  try {
    const parsedUrl = new URL(value, window.location.origin);

    parsedUrl.searchParams.forEach((paramValue, paramKey) => {
      if (SENSITIVE_KEY_PATTERN.test(paramKey)) {
        parsedUrl.searchParams.set(paramKey, "[redacted]");
      }
    });

    return parsedUrl.toString();
  } catch (error) {
    return value.replace(
      /([?&])(authorization|cookie|password|secret|token|api[-_]?key)=([^&]+)/gi,
      "$1$2=[redacted]"
    );
  }
};

const sanitizeValue = (value, depth = 0) => {
  if (value == null) {
    return value;
  }

  if (depth > 4) {
    return "[truncated]";
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => sanitizeValue(item, depth + 1));
  }

  if (value instanceof Headers) {
    return sanitizeHeaders(headersToObject(value));
  }

  if (typeof value === "object") {
    return Object.entries(value).reduce((accumulator, [key, currentValue]) => {
      accumulator[key] = SENSITIVE_KEY_PATTERN.test(key)
        ? "[redacted]"
        : URL_LIKE_KEY_PATTERN.test(key)
          ? sanitizeUrl(currentValue)
        : sanitizeValue(currentValue, depth + 1);

      return accumulator;
    }, {});
  }

  return sanitizeString(value);
};

const sanitizeHeaders = (headers) =>
  Object.entries(headers || {}).reduce((accumulator, [key, value]) => {
    accumulator[key] = SENSITIVE_KEY_PATTERN.test(key)
      ? "[redacted]"
      : sanitizeString(value);

    return accumulator;
  }, {});

export const headersToObject = (headers) => {
  if (!headers || typeof headers.forEach !== "function") {
    return {};
  }

  const mappedHeaders = {};

  headers.forEach((value, key) => {
    mappedHeaders[key] = value;
  });

  return sanitizeHeaders(mappedHeaders);
};

const normalizeError = (error, fallbackMessage = "Unknown error") => {
  if (error instanceof Error) {
    return error;
  }

  const normalizedError = new Error(
    error?.statusText || error?.message || fallbackMessage
  );

  if (error && typeof error === "object") {
    Object.entries(error).forEach(([key, value]) => {
      normalizedError[key] = sanitizeValue(value);
    });
  }

  return normalizedError;
};

const getConnectionContext = () => {
  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;

  if (!connection) {
    return {
      supported: false,
    };
  }

  return sanitizeValue({
    supported: true,
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
  });
};

const getServiceWorkerContext = () => {
  if (!("serviceWorker" in navigator)) {
    return {
      supported: false,
    };
  }

  const controller = navigator.serviceWorker.controller;

  return sanitizeValue({
    supported: true,
    controllerState: controller?.state || null,
    controllerScriptURL: controller?.scriptURL || null,
  });
};

const getFeatureContext = () =>
  sanitizeValue({
    fetch: typeof window.fetch === "function",
    localStorage: typeof window.localStorage !== "undefined",
    serviceWorker: "serviceWorker" in navigator,
    blob: typeof window.Blob !== "undefined",
    createObjectURL:
      typeof window.URL !== "undefined" &&
      typeof window.URL.createObjectURL === "function",
  });

const getTokenPayload = () => {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      return null;
    }

    return jwt_decode(token);
  } catch (error) {
    return null;
  }
};

const getSentryUserFromToken = () => {
  const payload = getTokenPayload();

  if (!payload) {
    return null;
  }

  const user = {
    id: payload.sub || payload.id || payload.user_id || null,
    email: payload.email || null,
    username: payload.name || payload.username || null,
  };

  return Object.values(user).some(Boolean) ? sanitizeValue(user) : null;
};

const setBaseScopeData = () => {
  Sentry.setTags({
    "app.name": "bb-portal-fe",
    "app.environment": SENTRY_ENVIRONMENT,
    "browser.name": browserName || "unknown",
    "browser.version": browserVersion || "unknown",
    "device.type": deviceType || (isMobile ? "mobile" : isTablet ? "tablet" : "desktop"),
    "os.name": osName || "unknown",
    "os.version": osVersion || "unknown",
  });

  Sentry.setContext(
    "browser",
    sanitizeValue({
      userAgent: navigator.userAgent,
      browserName: browserName || "unknown",
      browserVersion: browserVersion || "unknown",
      osName: osName || "unknown",
      osVersion: osVersion || "unknown",
      deviceType: deviceType || (isMobile ? "mobile" : isTablet ? "tablet" : "desktop"),
      language: navigator.language,
      languages: navigator.languages || [],
      online: navigator.onLine,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
  );

  Sentry.setContext("connection", getConnectionContext());
  Sentry.setContext("service_worker", getServiceWorkerContext());
  Sentry.setContext("feature_support", getFeatureContext());

  const sentryUser = getSentryUserFromToken();

  if (sentryUser) {
    Sentry.setUser(sentryUser);
  }
};

const sanitizeBreadcrumb = (breadcrumb) => {
  const nextBreadcrumb = {
    ...breadcrumb,
    data: sanitizeValue(breadcrumb.data),
  };

  if (nextBreadcrumb.category === "fetch" || nextBreadcrumb.category === "xhr") {
    nextBreadcrumb.data = sanitizeValue(nextBreadcrumb.data);
  }

  return nextBreadcrumb;
};

const sanitizeEvent = (event) => {
  const nextEvent = {
    ...event,
  };

  if (nextEvent.request?.headers) {
    nextEvent.request.headers = sanitizeHeaders(nextEvent.request.headers);
  }

  if (nextEvent.request?.url) {
    nextEvent.request.url = sanitizeUrl(nextEvent.request.url);
  }

  if (Array.isArray(nextEvent.breadcrumbs)) {
    nextEvent.breadcrumbs = nextEvent.breadcrumbs.map((breadcrumb) =>
      sanitizeBreadcrumb(breadcrumb)
    );
  }

  if (nextEvent.user) {
    nextEvent.user = sanitizeValue(nextEvent.user);
  }

  return nextEvent;
};

const addRuntimeListeners = () => {
  window.addEventListener("online", () => {
    addSentryBreadcrumb({
      category: "network",
      message: "Browser is back online",
      data: {
        online: true,
      },
    });
    Sentry.setContext("connection", getConnectionContext());
  });

  window.addEventListener("offline", () => {
    addSentryBreadcrumb({
      category: "network",
      message: "Browser went offline",
      level: "warning",
      data: {
        online: false,
      },
    });
    Sentry.setContext("connection", getConnectionContext());
  });

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        Sentry.setContext(
          "service_worker",
          sanitizeValue({
            ...getServiceWorkerContext(),
            scope: registration.scope,
            activeState: registration.active?.state || null,
          })
        );
      })
      .catch(() => {
        // Ignore SW readiness failures here; they are captured where registration happens.
      });
  }
};

export const isSentryEnabled = Boolean(SENTRY_DSN);

export const initSentry = () => {
  if (!isSentryEnabled) {
    return;
  }

  if (API_URL) {
    TRACE_PROPAGATION_TARGETS.push(new RegExp(`^${escapeRegExp(API_URL)}`));
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    sendDefaultPii: true,
    attachStacktrace: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: asNumber(
      process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE,
      0.1
    ),
    replaysSessionSampleRate: asNumber(
      process.env.REACT_APP_SENTRY_REPLAY_SESSION_SAMPLE_RATE,
      0.1
    ),
    replaysOnErrorSampleRate: asNumber(
      process.env.REACT_APP_SENTRY_REPLAY_ERROR_SAMPLE_RATE,
      1
    ),
    tracePropagationTargets: TRACE_PROPAGATION_TARGETS,
    beforeBreadcrumb: sanitizeBreadcrumb,
    beforeSend: sanitizeEvent,
    initialScope: () => {
      setBaseScopeData();
    },
  });

  setBaseScopeData();
  addRuntimeListeners();

  addSentryBreadcrumb({
    category: "app.lifecycle",
    message: "Sentry initialized",
    data: {
      path: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      environment: SENTRY_ENVIRONMENT,
    },
  });
};

export const addSentryBreadcrumb = ({
  category,
  message,
  data = {},
  level = "info",
  type,
}) => {
  if (!isSentryEnabled) {
    return;
  }

  Sentry.addBreadcrumb({
    category,
    message,
    data: sanitizeValue(data),
    level,
    type,
  });
};

export const captureSentryMessage = (
  message,
  { level = "info", tags = {}, contexts = {}, extras = {}, fingerprint } = {}
) => {
  if (!isSentryEnabled) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel(level);
    Object.entries(tags).forEach(([key, value]) => {
      scope.setTag(key, value);
    });
    Object.entries(contexts).forEach(([key, value]) => {
      scope.setContext(key, sanitizeValue(value));
    });
    Object.entries(extras).forEach(([key, value]) => {
      scope.setExtra(key, sanitizeValue(value));
    });

    if (fingerprint) {
      scope.setFingerprint(fingerprint);
    }

    Sentry.captureMessage(message);
  });
};

export const captureSentryException = (
  error,
  { tags = {}, contexts = {}, extras = {}, fingerprint } = {}
) => {
  if (!isSentryEnabled) {
    return;
  }

  const normalizedError = normalizeError(error);

  Sentry.withScope((scope) => {
    Object.entries(tags).forEach(([key, value]) => {
      scope.setTag(key, value);
    });
    Object.entries(contexts).forEach(([key, value]) => {
      scope.setContext(key, sanitizeValue(value));
    });
    Object.entries(extras).forEach(([key, value]) => {
      scope.setExtra(key, sanitizeValue(value));
    });

    if (fingerprint) {
      scope.setFingerprint(fingerprint);
    }

    Sentry.captureException(normalizedError);
  });
};

export const captureRequestFailure = ({
  error,
  operation,
  url,
  method = "GET",
  resource,
  responseHeaders,
  extra = {},
}) => {
  captureSentryException(error, {
    tags: {
      area: "http",
      operation,
      resource: resource || "unknown",
      method,
    },
    contexts: {
      request: {
        url,
        method,
      },
      response: {
        headers: sanitizeValue(responseHeaders),
      },
    },
    extras: extra,
    fingerprint: ["bb-portal-http-failure", operation, resource || "unknown", method],
  });
};

const buildRecordSnapshot = (resource, record) => {
  if (resource === "events") {
    const hasVoucher = Boolean(record?.voucher);
    const hasRoomingList = Boolean(record?.rooming_list);

    return sanitizeValue({
      recordId: record?.id || null,
      updatedAt: record?.updated_at || null,
      orderName: record?.order?.name || null,
      eventType: record?.type || null,
      hasVoucher,
      voucherIssuedAt: record?.voucher?.issued_at || null,
      hasRoomingList,
      roomingListIssuedAt: record?.rooming_list?.issued_at || null,
      documentState:
        hasVoucher || hasRoomingList ? "documents_present" : "documents_missing",
    });
  }

  const filesCount = Array.isArray(record?.files) ? record.files.length : 0;
  const filesShape =
    typeof record?.files === "undefined"
      ? "missing"
      : Array.isArray(record?.files)
        ? "array"
        : "invalid";
  const hasInfoSheet = Boolean(record?.info_sheet);

  return sanitizeValue({
    recordId: record?.id || null,
    updatedAt: record?.updated_at || null,
    hasInfoSheet,
    infoSheetIssuedAt: record?.info_sheet?.issued_at || null,
    filesCount,
    filesShape,
    fileNames: Array.isArray(record?.files)
      ? record.files.slice(0, 10).map((file) => file?.attach_name || null)
      : [],
    documentState:
      hasInfoSheet || filesCount > 0 ? "documents_present" : "documents_missing",
  });
};

const isSuspiciousSnapshot = (resource, snapshot) => {
  if (resource === "orders") {
    return snapshot.filesShape === "invalid";
  }

  if (snapshot.documentState === "documents_missing") {
    return true;
  }

  return false;
};

export const captureRecordSnapshot = ({
  resource,
  record,
  requestUrl,
  responseStatus,
  responseHeaders,
  source,
}) => {
  if (!isSentryEnabled || !MONITORED_RESOURCES.has(resource) || !record?.id) {
    return;
  }

  const snapshot = buildRecordSnapshot(resource, record);
  const snapshotKey = `${source}:${resource}:${record.id}:${record.updated_at || ""}:${snapshot.documentState}:${snapshot.filesCount || 0}:${snapshot.hasVoucher || false}:${snapshot.hasRoomingList || false}`;

  addSentryBreadcrumb({
    category: "bb.data",
    message: `${resource} record loaded`,
    data: {
      source,
      requestUrl,
      responseStatus,
      ...snapshot,
    },
  });

  if (!isSuspiciousSnapshot(resource, snapshot) || snapshotCache.has(snapshotKey)) {
    return;
  }

  snapshotCache.add(snapshotKey);

  captureSentryMessage(`${resource} record has suspicious document state`, {
    level: "warning",
    tags: {
      area: "data",
      resource,
      source,
      document_state: snapshot.documentState,
    },
    contexts: {
      record_snapshot: snapshot,
      response: {
        status: responseStatus,
        headers: sanitizeValue(responseHeaders),
      },
    },
    extras: {
      requestUrl,
    },
    fingerprint: ["bb-portal-missing-documents", resource, snapshot.documentState],
  });
};

export const syncSentryUserFromToken = () => {
  if (!isSentryEnabled) {
    return;
  }

  const sentryUser = getSentryUserFromToken();

  if (sentryUser) {
    Sentry.setUser(sentryUser);
    return;
  }

  Sentry.setUser(null);
};

export const clearSentryUser = () => {
  if (!isSentryEnabled) {
    return;
  }

  Sentry.setUser(null);
};
